import * as React from 'react';
import { KeyboardEvent } from 'react';
import { MaybePartialDate, PartialDate, partialDateToString } from '@tcl-argon-models/models';
import { getDayInMonthPlacement, getDayInWeekPlacement, mergeClasses } from '@tcl-argon-utils/utils/';
import { getCalendarData, isDateInSelectableRange, isDateInExcludedDates } from '@tcl-argon-utils/utils/';
import { CalendarCell } from './CalendarCell';
import { Header } from './Header';
import { useCalendarStyles } from './styles';
import { CalendarCellBackdropStyle, CalendarCellHoverState, DateRangePlacement } from './types';

type HTMLDivProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export interface CalendarProps extends HTMLDivProps {
  classes?: Record<string, string>;
  hideDaysOutsideCurrentMonth: boolean;
  isDateOutlined: (date: PartialDate) => boolean;
  isDateSelected: (date: PartialDate) => boolean;
  month: number;
  year: number;
  today: PartialDate;
  onDecrementDay: (event: KeyboardEvent<HTMLInputElement>) => void;
  onDecrementWeek: (event: KeyboardEvent<HTMLInputElement>) => void;
  onIncrementDay: (event: KeyboardEvent<HTMLInputElement>) => void;
  onIncrementWeek: (event: KeyboardEvent<HTMLInputElement>) => void;
  onSelectCurrentDate: (event: KeyboardEvent<HTMLInputElement>) => void;
  onSelectDate: (date: PartialDate) => void;
  onMouseEnterCalendarCell: (date: PartialDate) => void;
  onMouseLeaveCalendarCell: (date: PartialDate) => void;
  getCalendarCellHoverStyle: (date: PartialDate) => CalendarCellHoverState;
  getDateRangePlacement: (date: PartialDate) => DateRangePlacement;
  getCalendarCellBackdropStyle: (date: PartialDate) => CalendarCellBackdropStyle;
  minDate: MaybePartialDate;
  maxDate: MaybePartialDate;
  excludedDates: Array<{ startDateString: string; endDateString: string }>;
}

export const Calendar: React.FC<CalendarProps> = ({
  className = '',
  classes: classOverrides = {},
  hideDaysOutsideCurrentMonth,
  isDateOutlined,
  isDateSelected,
  month,
  year,
  today,
  onDecrementDay: handleDecrementDay,
  onDecrementWeek: handleDecrementWeek,
  onIncrementDay: handleIncrementDay,
  onIncrementWeek: handleIncrementWeek,
  onSelectCurrentDate: handleSelectCurrentDate,
  onMouseLeaveCalendarCell: handleMouseLeaveCalendarCell,
  onMouseEnterCalendarCell: handleMouseEnterCalendarCell,
  onSelectDate: handleSelectDate,
  getDateRangePlacement,
  getCalendarCellHoverStyle = () => CalendarCellHoverState.NO_HOVER_STATE,
  getCalendarCellBackdropStyle = () => CalendarCellBackdropStyle.NO_BACKDROP,
  minDate,
  maxDate,
  excludedDates,
  ...props
}) => {
  const classes = mergeClasses(useCalendarStyles(), classOverrides, { root: className });
  const {
    daysPrecedingSelectedMonth,
    daysInSelectedMonth,
    daysFollowingSelectedMonth,
    previousPartialDate,
    currentPartialDate,
    followingPartialDate,
  } = getCalendarData(month, year);

  const isInSelectableRange = (date: PartialDate, startDate: MaybePartialDate, endDate: MaybePartialDate): boolean => {
    if (startDate && startDate.isValid && endDate && endDate.isValid) {
      const dateString = date.month + '/' + date.day + '/' + date.year;
      const startDateString =
        startDate.dateObject.month + '/' + startDate.dateObject.day + '/' + startDate.dateObject.year;
      const endDateString = endDate.dateObject.month + '/' + endDate.dateObject.day + '/' + endDate.dateObject.year;
      return isDateInSelectableRange(dateString, startDateString, endDateString);
    } else {
      return true;
    }
  };

  const isDateExcluded = (
    date: PartialDate,
    excludedDates: Array<{ startDateString: string; endDateString: string }>,
  ): boolean => {
    const dateString = date.month + '/' + date.day + '/' + date.year;
    return isDateInExcludedDates(dateString, excludedDates);
  };

  const keyDownHandler = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowRight':
        handleIncrementDay(e);
        e.preventDefault();
        break;
      case 'ArrowLeft':
        handleDecrementDay(e);
        e.preventDefault();
        break;
      case 'ArrowUp':
        handleDecrementWeek(e);
        e.preventDefault();
        break;
      case 'ArrowDown':
        handleIncrementWeek(e);
        e.preventDefault();
        break;
      case 'T':
      case 't':
        handleSelectCurrentDate(e);
        e.preventDefault();
        break;
      default:
        break;
    }
  };

  return (
    <div className={classes.root} {...props} tabIndex={0} onKeyDown={keyDownHandler}>
      <Header />
      <div className={classes.days}>
        {daysPrecedingSelectedMonth.map((day) => {
          const date = { ...previousPartialDate, day };

          return (
            <CalendarCell
              day={day}
              hide={hideDaysOutsideCurrentMonth}
              isOutsideCurrentMonth
              dateRangePlacement={getDateRangePlacement(date)}
              isOutlined={isDateOutlined(date)}
              isSelected={isDateSelected(date)}
              key={partialDateToString(date)}
              onClick={() => handleSelectDate({ day, month, year })}
            />
          );
        })}
        {daysInSelectedMonth.map((day) => {
          const date = { ...currentPartialDate, day };

          return (
            <CalendarCell
              hide={false}
              isOutsideCurrentMonth={false}
              isToday={day === today.day && month === today.month}
              day={day}
              isOutlined={isDateOutlined(date)}
              isSelected={isDateSelected(date)}
              dateRangePlacement={getDateRangePlacement(date)}
              key={partialDateToString(date)}
              dayInWeekPlacement={getDayInWeekPlacement(date)}
              dayInMonthPlacement={getDayInMonthPlacement(date)}
              onClick={() => handleSelectDate({ day, month, year })}
              onMouseEnter={() => handleMouseEnterCalendarCell({ day, month, year })}
              onMouseLeave={() => handleMouseLeaveCalendarCell({ day, month, year })}
              calendarCellHoverStyle={getCalendarCellHoverStyle({ day, month, year })}
              calendarCellBackdropStyle={getCalendarCellBackdropStyle({ day, month, year })}
              disabled={!isInSelectableRange(date, minDate, maxDate) || isDateExcluded(date, excludedDates)}
            />
          );
        })}
        {daysFollowingSelectedMonth.map((day) => {
          const date = { ...followingPartialDate, day };

          return (
            <CalendarCell
              day={day}
              hide={hideDaysOutsideCurrentMonth}
              dateRangePlacement={getDateRangePlacement(date)}
              isOutlined={isDateOutlined(date)}
              isOutsideCurrentMonth
              isSelected={isDateSelected(date)}
              key={partialDateToString(date)}
              onClick={() => handleSelectDate(date)}
            />
          );
        })}
      </div>
    </div>
  );
};
